import React, { useState, useEffect, useContext, useMemo } from 'react';
import { AppContext } from '../context/AppContext';
import ContractDocumentView from './ContractDocumentView';
import SignaturePadModal from './SignaturePadModal';
import { 
  ShieldCheck, 
  Lock, 
  CheckCircle2, 
  PenTool, 
  Stamp, 
  RotateCcw, 
  Download, 
  Printer, 
  Calendar, 
  Clock, 
  AlertCircle,
  FileCheck,
  Building2,
  Sparkles,
  Maximize2,
  X,
  Check,
  Paperclip,
  FileText,
  AlertTriangle,
  Home,
  CreditCard,
  UserCheck
} from 'lucide-react';
import { downloadContractPdf, printContractDocument } from '../utils/contractPdfHelper';

const PublicContractSignView = ({ token }) => {
  const { contracts, users, fetchRemoteContracts, signContract, approveResignationStep, rejectResignationStep, acceptContractDeletion, rejectContractDeletion } = useContext(AppContext);

  // Sync real-time contracts on mount
  useEffect(() => {
    if (fetchRemoteContracts) {
      fetchRemoteContracts();
    }
  }, [token]);

  // Find contract by token
  const contract = useMemo(() => {
    return contracts.find(c => c.PublicToken === token);
  }, [contracts, token]);

  const isResignation = Boolean(
    contract?.IsResignation || 
    contract?.ContractType === 'RESIGNATION' || 
    contract?.ContractType === 'RESIGNATION_NDA' || 
    contract?.ContractType === 'HANDOVER'
  );

  // Parse URL step parameter (e.g., ?step=1 or &step=1)
  const urlStep = useMemo(() => {
    const hash = window.location.hash || '';
    const search = window.location.search || '';
    const match = (hash + search).match(/[?&]step=(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  }, []);

  // Current resignation step in database/state
  const currentStepNum = contract?.CurrentStep || 1;
  const currentStepObj = useMemo(() => {
    if (!isResignation || !contract?.ApprovalLine) return null;
    return contract.ApprovalLine.find(s => s.Step === currentStepNum);
  }, [isResignation, contract, currentStepNum]);

  const urlStepObj = useMemo(() => {
    if (!isResignation || !contract?.ApprovalLine || !urlStep) return null;
    return contract.ApprovalLine.find(s => s.Step === urlStep);
  }, [isResignation, contract, urlStep]);

  const approverUser = useMemo(() => {
    if (!currentStepObj) return null;
    return (users || []).find(u => u.UserID === currentStepObj.ApproverID || u.Name === currentStepObj.ApproverName);
  }, [users, currentStepObj]);

  // Step state validations
  const isStepCompleted = Boolean(isResignation && urlStep !== null && urlStep < currentStepNum);
  const isFutureStep = Boolean(isResignation && urlStep !== null && urlStep > currentStepNum);
  const isExactCurrentStep = Boolean(!isResignation || urlStep === null || urlStep === currentStepNum);

  // Auth gate for public link
  const [authName, setAuthName] = useState('');
  const [authPhone, setAuthPhone] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isApproverRole, setIsApproverRole] = useState(false); // true if authenticated user is the step approver
  const [authError, setAuthError] = useState('');

  // Sign & Approval state
  const [agreed, setAgreed] = useState(false);
  const [approvalComment, setApprovalComment] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectBox, setShowRejectBox] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isApprovedNow, setIsApprovedNow] = useState(false);
  const [isSignaturePadOpen, setIsSignaturePadOpen] = useState(false);
  const [savedSignature, setSavedSignature] = useState(null); // { dataUrl, type }

  // Direct Employee Input: Resident Registration Number & Address
  const [residentNumber, setResidentNumber] = useState('');
  const [userAddress, setUserAddress] = useState('');

  // Synchronize when contract loads
  useEffect(() => {
    if (contract) {
      if (contract.ResidentRegistrationNumber || contract.residentNumber) {
        setResidentNumber(contract.ResidentRegistrationNumber || contract.residentNumber);
      }
      if (contract.UserAddress || contract.userAddress) {
        setUserAddress(contract.UserAddress || contract.userAddress);
      }
    }
  }, [contract]);

  const handleResidentNumberChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, '').slice(0, 13);
    if (raw.length <= 6) {
      setResidentNumber(raw);
    } else {
      setResidentNumber(`${raw.slice(0, 6)}-${raw.slice(6)}`);
    }
  };

  const previewContract = useMemo(() => {
    if (!contract) return null;
    return {
      ...contract,
      ResidentRegistrationNumber: residentNumber,
      UserAddress: userAddress
    };
  }, [contract, residentNumber, userAddress]);

  // If already signed / approved
  const isAlreadySigned = contract?.Status === 'SIGNED' || contract?.Status === 'APPROVED' || isApprovedNow;

  // Calculate days remaining for 15-day access
  const daysRemaining = useMemo(() => {
    if (!contract?.TokenExpiresAt) return 15;
    const diff = new Date(contract.TokenExpiresAt) - new Date();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  }, [contract]);

  // Handle Verify Identity (Name + Phone Last 4 Digits)
  const handleVerify = (e) => {
    e.preventDefault();
    if (!contract) {
      setAuthError('유효하지 않거나 만료된 계약서 링크입니다.');
      return;
    }

    const inputName = authName.trim();
    const inputPhone4 = authPhone.replace(/[^0-9]/g, '').slice(-4);

    if (isResignation) {
      // 1. Check current step approver
      const currentStepApproverName = currentStepObj?.ApproverName || approverUser?.Name;
      const isCurrentApproverMatch = Boolean(currentStepApproverName && inputName === currentStepApproverName);

      // 2. Check url step approver (for viewing completed step)
      const urlStepApproverName = urlStepObj?.ApproverName;
      const isPastApproverMatch = Boolean(urlStepApproverName && inputName === urlStepApproverName);

      // 3. Check applicant
      const isApplicant = inputName === contract.UserName;

      const targetPhone = (approverUser?.Phone || contract.UserPhone || '').replace(/[^0-9]/g, '');
      const targetPhone4 = targetPhone.slice(-4);
      const isPhoneMatch = !targetPhone4 || !inputPhone4 || targetPhone4 === inputPhone4;

      if (isCurrentApproverMatch && isPhoneMatch && !isStepCompleted) {
        setIsAuthenticated(true);
        setIsApproverRole(true);
        setAuthError('');
      } else if (isPastApproverMatch || isApplicant || isCurrentApproverMatch) {
        setIsAuthenticated(true);
        setIsApproverRole(false); // Read-only / view completed mode
        setAuthError('');
      } else {
        const expectedName = isStepCompleted ? (urlStepApproverName || '1차 승인자') : (currentStepApproverName || '담당자');
        setAuthError(`입력하신 정보가 ${isStepCompleted ? `${urlStep}단계 승인권자(${expectedName})` : `${currentStepNum}단계 결재권자(${currentStepObj?.RoleName || '승인자'}: ${expectedName})`} 또는 신청인 정보와 일치하지 않습니다.`);
      }
    } else {
      // Standard contract: check recipient name and phone last 4
      const targetPhone = (contract.UserPhone || '').replace(/[^0-9]/g, '');
      const targetPhone4 = targetPhone.slice(-4);

      const isNameMatch = inputName === contract.UserName;
      const isPhoneMatch = !targetPhone4 || !inputPhone4 || targetPhone4 === inputPhone4;

      if (isNameMatch && isPhoneMatch) {
        setIsAuthenticated(true);
        setIsApproverRole(false);
        setAuthError('');
      } else {
        setAuthError('입력하신 정보가 계약 대상자 정보와 일치하지 않습니다. 성명과 휴대폰 번호 뒤 4자리를 다시 확인해 주세요.');
      }
    }
  };

  const handleSignatureConfirmed = (dataUrl, type) => {
    setSavedSignature({ dataUrl, type });
  };

  // Submit Resignation Step Approval
  const handleApproveResignation = async () => {
    if (!contract) return;
    if (!isApproverRole) {
      alert('결재 승인 권한이 없습니다.');
      return;
    }
    try {
      setIsSubmitting(true);
      const approverInfo = approverUser || {
        Name: authName || currentStepObj?.ApproverName || '결재권자',
        UserID: approverUser?.UserID || `approver-${Date.now()}`
      };
      await approveResignationStep(contract.ContractID, approverInfo, approvalComment || '승인 완료');
      setIsApprovedNow(true);
      alert(`${currentStepNum}단계(${currentStepObj?.RoleName || '결재'}) 승인이 정상 처리되었습니다.`);
    } catch (e) {
      console.error(e);
      alert('승인 처리 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Submit Resignation Step Rejection
  const handleRejectResignation = async () => {
    if (!contract) return;
    if (!rejectReason.trim()) {
      alert('반려 사유를 입력해 주세요.');
      return;
    }
    try {
      setIsSubmitting(true);
      const approverInfo = approverUser || {
        Name: authName || currentStepObj?.ApproverName || '결재권자'
      };
      await rejectResignationStep(contract.ContractID, approverInfo, rejectReason);
      alert('사직원 결재가 반려 처리되었습니다.');
    } catch (e) {
      console.error(e);
      alert('반려 처리 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Submit Sign for regular contracts
  const handleSign = async () => {
    // 1. Validate Resident Number (Strict 13 digits)
    const cleanRrn = residentNumber.replace(/[^0-9]/g, '');
    if (!cleanRrn || cleanRrn.length !== 13) {
      alert('전자계약서 법적 효력 확보를 위해 본인의 주민등록번호 13자리를 정확히 입력해 주세요.');
      return;
    }

    // 2. Validate Address
    if (!userAddress.trim()) {
      alert('계약서에 명기될 본인의 주민등록상 실거주지 자택 주소를 직접 입력해 주세요.');
      return;
    }

    if (!savedSignature?.dataUrl) {
      alert('계약서 체결을 위해 [고해상도 전자서명/도장 날인] 버튼을 눌러 서명을 완료해 주세요.');
      setIsSignaturePadOpen(true);
      return;
    }

    if (isSubmitting) return;

    if (!agreed) {
      alert('계약 조항 확인 및 전자서명 동의를 체크해 주세요.');
      return;
    }

    try {
      setIsSubmitting(true);
      const ok = await signContract(contract.ContractID, savedSignature.dataUrl, {
        IpAddress: '211.234.88.12 (Public Mobile)',
        UserAgent: navigator.userAgent,
        ResidentRegistrationNumber: residentNumber,
        UserAddress: userAddress.trim()
      });

      if (ok) {
        alert('전자계약서 서명이 성공적으로 완료되었습니다!');
      }
    } catch (e) {
      console.error(e);
      alert('서명 처리 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!contract) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-slate-700 p-8 rounded-2xl max-w-md text-center space-y-4">
          <AlertCircle className="w-12 h-12 text-rose-400 mx-auto" />
          <h2 className="text-lg font-bold">계약서를 찾을 수 없습니다</h2>
          <p className="text-xs text-slate-400">
            링크가 만료되었거나 올바르지 않은 접근 토큰입니다. 발송자(주식회사 클로우플레이크)에게 문의해 주세요.
          </p>
        </div>
      </div>
    );
  }

  const isTokenExpired = useMemo(() => {
    if (!contract || !contract.TokenExpiresAt) return false;
    if (contract.Status === 'SIGNED' || contract.Status === 'APPROVED' || Boolean(contract.EmployeeSignedAt)) return false;
    return new Date(contract.TokenExpiresAt) < new Date();
  }, [contract]);

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 font-sans pb-16">
      {/* Expiration Notice Screen if Link Expired */}
      {isTokenExpired && (
        <div className="fixed inset-0 z-50 bg-slate-900/90 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-8 text-center space-y-5 shadow-2xl border border-slate-200">
            <div className="w-16 h-16 rounded-full bg-rose-100 border-2 border-rose-300 flex items-center justify-center mx-auto text-rose-600 animate-pulse">
              <Clock className="w-8 h-8" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-black text-slate-900">서명 링크 유효기간 만료</h2>
              <p className="text-xs text-slate-600 leading-relaxed">
                본 전자계약서 서명 링크는 보안 유효기간이 지났습니다.<br />
                <strong>만료 일시:</strong> {new Date(contract.TokenExpiresAt).toLocaleString('ko-KR')}
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 p-4 rounded-2xl text-xs text-rose-900 space-y-1 text-left">
              <p className="font-bold">안내사항:</p>
              <p>• 서명이 완료되지 않은 채 지정된 보안 기한이 만료되었습니다.</p>
              <p>• 계약 담당자(주식회사 클로우플레이크 인사팀)에 연락하여 링크 유효기간 연장을 요청해 주세요.</p>
            </div>

            <button
              type="button"
              onClick={() => {
                alert(`계약 담당자(hr@clouflake.com)에게 유효기간 재연장 및 서명 링크 재발송 요청 메일이 전달되었습니다.`);
              }}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer flex items-center justify-center gap-2"
            >
              <Send className="w-4 h-4" />
              <span>담당자에게 유효기간 재연장 요청 발송</span>
            </button>
          </div>
        </div>
      )}

      {/* Top Brand Bar */}
      <header className="bg-slate-900 text-white px-6 py-4 shadow-md sticky top-0 z-40">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-indigo-600 flex items-center justify-center text-white font-black text-sm">
              C
            </div>
            <div className="text-left">
              <span className="text-xs font-black tracking-widest text-indigo-400 uppercase">Clouflake</span>
              <h1 className="text-sm font-bold leading-tight">전자계약 포털</h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[11px] bg-slate-800 text-slate-300 px-3 py-1 rounded-lg font-mono border border-slate-700">
              보안 인증 링크
            </span>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="max-w-4xl mx-auto p-4 md:p-6 space-y-6">
        
        {/* Step 1: Authentication Gate if not verified */}
        {!isAuthenticated && (
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-slate-200 text-center max-w-md mx-auto space-y-6 my-8">
            <div className="w-14 h-14 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto border border-indigo-100 shadow-xs">
              <Lock className="w-7 h-7" />
            </div>

            <div className="space-y-1">
              <h2 className="text-base font-bold text-slate-900">
                {isResignation ? (isStepCompleted ? `사직원 열람 본인확인 (${urlStep}단계 완료)` : `사직원 결재 본인확인 (${currentStepNum}/4단계)`) : '본인 확인 및 계약서 열람'}
              </h2>
              <p className="text-xs text-slate-500">
                {isResignation
                  ? (isStepCompleted ? '이전 승인권자 또는 신청인의 성명과 휴대폰 번호 뒤 4자리를 입력하여 열람하세요.' : `${currentStepObj?.RoleName || '결재권자'}님의 성명과 휴대폰 번호 뒤 4자리를 입력해 주세요.`)
                  : '기밀 보호를 위해 계약 대상자의 성명과 휴대폰 번호 뒤 4자리를 입력해 주세요.'}
              </p>
            </div>

            {/* Withdrawn Notice in Auth Gate */}
            {contract?.Status === 'WITHDRAWN' && (
              <div className="bg-slate-100 border border-slate-300 rounded-2xl p-4 text-left flex items-start gap-3">
                <RotateCcw className="w-5 h-5 text-slate-600 shrink-0 mt-0.5" />
                <div>
                  <strong className="text-xs font-bold text-slate-900 block mb-0.5">
                    [퇴사 취하 완료] 본 사직원은 신청인에 의해 정식 취하되었습니다.
                  </strong>
                  <p className="text-[11px] text-slate-600 leading-relaxed">
                    신청인(<strong>{contract.UserName}</strong>)이 회사 잔류를 결정하고 사직원을 공식 취하(철회)하여 결재 프로세스가 완전히 종료되었습니다. <strong>더 이상 결재를 진행하실 필요가 없습니다.</strong> (성명/휴대폰 번호 입력 시 취하된 전문 열람 가능)
                  </p>
                </div>
              </div>
            )}

            {isStepCompleted && contract?.Status !== 'WITHDRAWN' && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-left flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <div className="text-[11px] text-emerald-800 leading-relaxed">
                  <strong>[{urlStep}단계: {contract?.ApprovalLine?.[urlStep - 1]?.RoleName || '승인'}]</strong> 승인 처리가 이미 완료된 단계입니다. (현재 <strong>{currentStepNum}단계: {currentStepObj?.RoleName || '다음 단계'}</strong> 진행 중)
                </div>
              </div>
            )}

            <form onSubmit={handleVerify} className="space-y-4 text-left">
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">성명 *</label>
                <input
                  type="text"
                  required
                  value={authName}
                  onChange={e => setAuthName(e.target.value)}
                  placeholder={isResignation ? (isStepCompleted ? `예: ${urlStepObj?.ApproverName || '1차 승인자'}` : `예: ${currentStepObj?.ApproverName || '홍길동'}`) : '예: 홍길동 또는 박프리'}
                  className="w-full text-xs font-bold px-3.5 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">휴대폰 번호 뒤 4자리 *</label>
                <input
                  type="text"
                  maxLength={4}
                  required
                  value={authPhone}
                  onChange={e => setAuthPhone(e.target.value)}
                  placeholder="예: 1234 (숫자 4자리)"
                  className="w-full text-xs font-bold px-3.5 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 font-mono tracking-wider"
                />
              </div>

              {authError && (
                <div className="p-3 bg-rose-50 text-rose-700 rounded-xl text-xs flex items-center gap-2 border border-rose-200">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{authError}</span>
                </div>
              )}

              <button
                type="submit"
                className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-md transition-all active:scale-98 cursor-pointer"
              >
                {isResignation ? '결재 문서 확인하기' : '계약서 확인하기'}
              </button>
            </form>
          </div>
        )}

        {/* Step 2 & 3: Full Contract Review & Signing/Approval Area */}
        {isAuthenticated && (
          <div className="space-y-6">
            {/* Status Notice Banner */}
            {contract.Status === 'WITHDRAWN' ? (
              <div className="bg-slate-100 border-2 border-slate-300 rounded-2xl p-4 md:p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-sm text-left">
                <div className="flex items-center gap-3.5">
                  <div className="w-11 h-11 rounded-xl bg-slate-700 text-white flex items-center justify-center shrink-0 shadow-xs">
                    <RotateCcw className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold text-slate-900">
                        사직원 퇴사 취하(철회) 완료 - 결재 프로세스 종료
                      </h3>
                      <span className="text-[10px] font-bold text-slate-700 bg-slate-200 border border-slate-300 px-2 py-0.5 rounded">
                        취하 완료
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                      신청인(<strong>{contract.UserName}</strong>)이 회사 잔류를 결정하여 사직원을 공식 취하하였으므로, <strong>더 이상 결재 승인을 진행하지 않습니다.</strong>
                    </p>
                    {contract.WithdrawnAt && (
                      <div className="text-[11px] text-slate-500 mt-1 font-mono">
                        취하일시: {new Date(contract.WithdrawnAt).toLocaleString('ko-KR')}
                        {contract.WithdrawalReason && ` | 취하 사유: ${contract.WithdrawalReason}`}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : isStepCompleted ? (
              <div className="bg-emerald-50 border-2 border-emerald-300 rounded-2xl p-4 md:p-5 flex items-center justify-between text-left shadow-xs">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-emerald-950">
                      [{urlStep}단계: {contract.ApprovalLine?.[urlStep - 1]?.RoleName || '승인'}] 승인 처리가 완료된 단계입니다.
                    </h3>
                    <p className="text-xs text-emerald-700 mt-0.5">
                      해당 결재 단계는 정상 완료되었으며, 현재 사직원은 <strong>{currentStepNum}단계({currentStepObj?.RoleName || '결재'})</strong> 진행 중입니다.
                    </p>
                  </div>
                </div>
              </div>
            ) : isFutureStep ? (
              <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 md:p-5 flex items-center justify-between text-left shadow-xs">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0">
                    <Clock className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-amber-950">
                      [{urlStep}단계] 이전 단계 결재 대기 중
                    </h3>
                    <p className="text-xs text-amber-700 mt-0.5">
                      이전 결재 단계({currentStepNum}단계: {currentStepObj?.RoleName || '승인자'}) 승인이 완료된 후 본 결재를 진행하실 수 있습니다.
                    </p>
                  </div>
                </div>
              </div>
            ) : isAlreadySigned ? (
              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-4 md:p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-sm">
                <div className="flex items-center gap-3 text-left">
                  <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-emerald-950">
                      {isResignation ? `사직원 ${currentStepNum}단계 결재 승인 완료` : '전자계약 체결이 완료된 문서입니다.'}
                    </h3>
                    <p className="text-xs text-emerald-700 mt-0.5">
                      {isResignation
                        ? `승인 처리 완료일시: ${new Date().toLocaleString('ko-KR')}`
                        : `체결일시: ${new Date(contract.EmployeeSignedAt || contract.CompanySignedAt).toLocaleString('ko-KR')}`} | 안전하게 암호화 보관 중
                    </p>
                  </div>
                </div>
              </div>
            ) : contract.Status === 'REJECTED' ? (
              <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 flex items-center gap-3 text-left">
                <AlertCircle className="w-5 h-5 text-rose-600 shrink-0" />
                <div>
                  <h3 className="text-xs font-bold text-rose-900">결재가 반려된 문서입니다.</h3>
                  <p className="text-[11px] text-rose-700">반려 사유: {contract.RejectReason || '사유 미기재'}</p>
                </div>
              </div>
            ) : isResignation && !isApproverRole ? (
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-center gap-3 text-left">
                <ShieldCheck className="w-5 h-5 text-blue-600 shrink-0" />
                <div>
                  <h3 className="text-xs font-bold text-blue-900">[신청인 열람 모드] 본인이 신청한 사직원 문서입니다.</h3>
                  <p className="text-[11px] text-blue-700">
                    현재 <strong>{currentStepNum}단계 결재권자({currentStepObj?.RoleName || '승인자'}: {currentStepObj?.ApproverName})</strong>의 승인을 기다리는 중입니다.
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center justify-between text-left">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
                  <div>
                    <h3 className="text-xs font-bold text-amber-900">
                      {isResignation ? `[${currentStepNum}/4단계: ${currentStepObj?.RoleName || '결재'}] 결재 승인 대상자입니다.` : '서명 대기 중인 전자계약서입니다.'}
                    </h3>
                    <p className="text-[11px] text-amber-700">
                      {isResignation ? '문서 내용을 검토하신 후 하단에서 결재 승인 또는 반려를 진행해 주세요.' : '문서 내용을 끝까지 확인하신 후 하단에서 서명을 진행해 주세요.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Attached Handover Documents Quick Card */}
            {(() => {
              const attachedList = Array.isArray(contract?.AttachedFiles) && contract.AttachedFiles.length > 0 
                ? contract.AttachedFiles 
                : (Array.isArray(contract?.HandoverFiles) && contract.HandoverFiles.length > 0 
                    ? contract.HandoverFiles 
                    : (Array.isArray(contract?.Attachments) && contract.Attachments.length > 0 ? contract.Attachments : []));

              if (attachedList.length === 0) return null;

              return (
                <div className="bg-white border-2 border-indigo-200 rounded-2xl p-4 md:p-5 shadow-xs space-y-3 text-left animate-in fade-in duration-200">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                    <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <Paperclip className="w-4 h-4 text-indigo-600" />
                      신청인이 첨부한 인수인계 산출물 및 증빙 자료 ({attachedList.length}건)
                    </span>
                    <span className="text-[11px] text-indigo-700 font-bold bg-indigo-50 px-2 py-0.5 rounded">
                      다운로드 가능
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {attachedList.map((file, fIdx) => (
                      <div key={file.id || fIdx} className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center justify-between gap-3 hover:bg-slate-100/80 transition-colors">
                        <div className="flex items-center gap-2.5 overflow-hidden">
                          <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center shrink-0 font-bold">
                            <FileText className="w-4 h-4" />
                          </div>
                          <div className="overflow-hidden">
                            <div className="text-xs font-bold text-slate-900 truncate" title={file.name}>
                              {file.name}
                            </div>
                            <div className="text-[10px] text-slate-400 font-mono">
                              {file.size ? (file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(1)} KB` : `${(file.size / (1024 * 1024)).toFixed(1)} MB`) : '첨부파일'}
                            </div>
                          </div>
                        </div>

                        {file.dataUrl && (
                          <a
                            href={file.dataUrl}
                            download={file.name || '인수인계_첨부문서'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-[11px] font-bold rounded-lg flex items-center gap-1 shrink-0 shadow-2xs transition-all cursor-pointer"
                          >
                            <Download className="w-3.5 h-3.5" />
                            다운로드
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Document Render (Single Render with inline approval hidden) */}
            <div className="shadow-md rounded-2xl overflow-hidden">
              <ContractDocumentView contract={previewContract || contract} hideInlineApproval={true} />
            </div>

            {/* Resignation Approval Action Card (Strictly only for current step's verified approver) */}
            {isResignation && isExactCurrentStep && isApproverRole && !isAlreadySigned && contract.Status !== 'REJECTED' && contract.Status !== 'WITHDRAWN' && (
              <div className="bg-white border-2 border-indigo-200 rounded-2xl p-4 md:p-6 shadow-sm space-y-4 text-left animate-in fade-in duration-200">
                <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                  <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                    <ShieldCheck className="w-4 h-4 text-indigo-600" />
                    사직원 {currentStepNum}단계 결재 승인 ({currentStepObj?.RoleName})
                  </span>
                  <span className="text-[11px] text-indigo-700 font-bold">
                    결재자: {authName || currentStepObj?.ApproverName}
                  </span>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-[11px] font-bold text-slate-700 block mb-1">결재 의견 / 비고 (선택)</label>
                    <input
                      type="text"
                      value={approvalComment}
                      onChange={e => setApprovalComment(e.target.value)}
                      placeholder="인수인계 확인 및 결재 의견을 입력하세요 (기본: 승인 완료)"
                      className="w-full px-3 py-2 text-xs border border-slate-300 rounded-xl focus:border-indigo-600 focus:outline-none"
                    />
                  </div>

                  {showRejectBox ? (
                    <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl space-y-2">
                      <label className="text-[11px] font-bold text-rose-800 block">반려 사유 입력 *</label>
                      <textarea
                        rows={2}
                        value={rejectReason}
                        onChange={e => setRejectReason(e.target.value)}
                        placeholder="사직원 결재 반려 사유를 상세히 입력하세요."
                        className="w-full p-2 text-xs border border-rose-300 rounded-lg bg-white resize-none"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={isSubmitting}
                          onClick={handleRejectResignation}
                          className="flex-1 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-lg cursor-pointer"
                        >
                          반려 확정하기
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowRejectBox(false)}
                          className="px-3 py-2 bg-slate-200 text-slate-700 text-xs font-bold rounded-lg cursor-pointer"
                        >
                          취소
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-3 pt-2">
                      <button
                        type="button"
                        disabled={isSubmitting}
                        onClick={handleApproveResignation}
                        className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-md cursor-pointer flex items-center justify-center gap-2 active:scale-98 transition-transform"
                      >
                        <Check className="w-4 h-4" />
                        <span>{isSubmitting ? '처리 중...' : `[${currentStepNum}/4단계] 결재 승인하기`}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowRejectBox(true)}
                        className="px-4 py-3 bg-slate-100 hover:bg-rose-50 text-slate-600 hover:text-rose-700 text-xs font-bold rounded-xl border border-slate-200 cursor-pointer"
                      >
                        반려
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Standard Contract Signing Interactive Toolbox */}
            {!isResignation && !isAlreadySigned && (
              <div className="bg-white border-2 border-indigo-200 rounded-2xl p-4 md:p-6 shadow-sm space-y-4">
                {/* Mandatory Direct Employee Input Section */}
                <div className="bg-slate-50 border border-indigo-100 rounded-2xl p-4 md:p-5 space-y-3.5 text-left">
                  <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                    <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <UserCheck className="w-4 h-4 text-indigo-600" />
                      계약 당사자 인적사항 직접 기재 (필수)
                    </span>
                    <span className="text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-200 font-bold px-2 py-0.5 rounded">
                      본인 직접 작성
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-600 leading-relaxed">
                    근로기준법 및 계약 체결 법적 효력 확보를 위해 본인의 <strong>주민등록번호</strong>와 <strong>자택 주소</strong>를 직접 작성해 주세요. (기재 시 상단 계약서에 즉시 반영됩니다.)
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div>
                      <label className="text-[11px] font-bold text-slate-700 flex items-center gap-1 mb-1">
                        <CreditCard className="w-3.5 h-3.5 text-indigo-600" />
                        주민등록번호 (13자리) *
                      </label>
                      <input
                        type="text"
                        maxLength={14}
                        placeholder="예: 950101-1234567"
                        value={residentNumber}
                        onChange={handleResidentNumberChange}
                        className="w-full px-3 py-2 bg-white border border-slate-300 focus:border-indigo-600 rounded-xl font-mono text-xs text-slate-900 font-bold tracking-wider outline-none"
                      />
                      <span className="text-[10px] text-slate-400 mt-1 block">
                        * 체결 후 기본 뷰/인쇄 시 뒷자리는 마스킹(950101-1******) 처리되어 안전하게 보관됩니다.
                      </span>
                    </div>
                    <div>
                      <label className="text-[11px] font-bold text-slate-700 flex items-center gap-1 mb-1">
                        <Home className="w-3.5 h-3.5 text-indigo-600" />
                        자택 주소 (실거주지) *
                      </label>
                      <input
                        type="text"
                        placeholder="예: 서울시 강서구 양천로 123, 101동 202호"
                        value={userAddress}
                        onChange={e => setUserAddress(e.target.value)}
                        className="w-full px-3 py-2 bg-white border border-slate-300 focus:border-indigo-600 rounded-xl text-xs text-slate-900 font-semibold outline-none"
                      />
                      <span className="text-[10px] text-slate-400 mt-1 block">
                        * 주민등록상 거주지 주소를 동·호수까지 상세히 작성해 주세요.
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                  <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                    <PenTool className="w-4 h-4 text-indigo-600" />
                    전자서명 또는 도장 날인 *
                  </span>
                  <span className="text-[11px] text-slate-500">
                    서명자: <strong className="text-slate-900">{contract.UserName}</strong>
                  </span>
                </div>

                {savedSignature ? (
                  <div className="p-4 bg-slate-50 border border-indigo-200 rounded-2xl flex items-center justify-between shadow-2xs">
                    <div className="flex items-center gap-3">
                      <div className="w-24 h-16 bg-white border border-slate-200 rounded-xl flex items-center justify-center p-1 overflow-hidden">
                        <img src={savedSignature.dataUrl} alt="서명 미리보기" className="max-w-full max-h-full object-contain" />
                      </div>
                      <div>
                        <span className="text-xs font-bold text-emerald-700 flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          {savedSignature.type === 'DRAW' ? '고해상도 자필 서명 날인 완료' : '공인 전자도장 날인 완료'}
                        </span>
                        <span className="text-[11px] text-slate-500 block mt-0.5">정상적으로 전자서명 데이터가 생성되었습니다.</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setIsSignaturePadOpen(true)}
                        className="px-3 py-1.5 bg-white hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl cursor-pointer border border-slate-200"
                      >
                        다시 서명
                      </button>
                      <button
                        type="button"
                        onClick={() => setSavedSignature(null)}
                        className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-xl cursor-pointer"
                        title="서명 삭제"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-6 bg-slate-50 border-2 border-dashed border-indigo-200 rounded-2xl flex flex-col items-center justify-center text-center space-y-3">
                    <div className="w-12 h-12 rounded-2xl bg-indigo-100 flex items-center justify-center text-indigo-600 shadow-2xs">
                      <PenTool className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-900">전자서명 또는 도장 날인이 필요합니다</h4>
                      <p className="text-[11px] text-slate-500">모바일 터치 및 마우스 필압 스무딩 서명과 자동 도장 생성을 지원합니다.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsSignaturePadOpen(true)}
                      className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-md cursor-pointer flex items-center gap-2 transition-transform active:scale-95"
                    >
                      <Maximize2 className="w-4 h-4" />
                      <span>✍️ 고해상도 서명 패드 열기</span>
                    </button>
                  </div>
                )}

                {/* Legal Agree Checkbox */}
                <div className="pt-3 border-t border-slate-200">
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={e => setAgreed(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded text-indigo-600 border-slate-300"
                    />
                    <span className="text-xs text-slate-700 leading-relaxed font-medium">
                      본인은 계약서의 모든 조항을 열람 및 숙지하였으며, 전자서명법에 따른 전자서명 및 날인에 최종 동의합니다.
                    </span>
                  </label>
                </div>

                <button
                  type="button"
                  disabled={!agreed || isSubmitting}
                  onClick={handleSign}
                  className={`w-full py-3.5 rounded-xl text-xs font-bold text-white transition-all shadow-md flex items-center justify-center gap-2 ${
                    agreed && !isSubmitting
                      ? 'bg-indigo-600 hover:bg-indigo-700 cursor-pointer active:scale-98'
                      : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  }`}
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {isSubmitting ? '서명 체결 처리 중...' : '계약서 서명 및 체결 완료하기'}
                </button>
              </div>
            )}

            {/* Requirement 1: Deletion Agreement Requested Notice Banner */}
            {contract.Status === 'DELETE_REQUESTED' && (
              <div className="bg-rose-50 border-2 border-rose-200 rounded-2xl p-5 text-left space-y-3 animate-in fade-in">
                <div className="flex items-center gap-2 text-rose-800 font-extrabold text-sm">
                  <AlertTriangle className="w-5 h-5 text-rose-600 flex-shrink-0" />
                  <span>[계약서 삭제/파기 합의 요청 대기 중]</span>
                </div>
                <p className="text-xs text-slate-700 leading-relaxed">
                  상대 당사자(주식회사 클로우플레이크)가 체결된 본 계약서의 삭제/파기 합의를 요청했습니다.<br />
                  <strong>사유:</strong> {contract.DeleteRequestedReason || '상호 계약 해지 및 파기 합의 요청'}
                </p>
                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={async () => {
                      if (confirm('계약서 삭제 합의 요청을 거절하시겠습니까? (계약이 유지됩니다)')) {
                        await rejectContractDeletion(contract.ContractID);
                        alert('삭제 합의 요청을 거절했습니다.');
                      }
                    }}
                    className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs font-bold rounded-xl"
                  >
                    삭제 거절 (계약 유지)
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (confirm('계약서 파기/삭제에 합의하여 영구 삭제하시겠습니까?')) {
                        await acceptContractDeletion(contract.ContractID);
                        alert('양측 상호 합의에 의해 계약서가 영구 파기되었습니다.');
                      }
                    }}
                    className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl shadow-2xs"
                  >
                    삭제 합의 수락 및 영구 파기
                  </button>
                </div>
              </div>
            )}

            {/* Print / Download Buttons if Signed */}
            {isAlreadySigned && (
              <div className="flex flex-wrap justify-center gap-3">
                <button
                  type="button"
                  onClick={() => downloadContractPdf(document.getElementById(`contract-doc-${contract.ContractID}`), contract.Title)}
                  className="flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-md transition-all cursor-pointer"
                  title="PDF 파일 다운로드"
                >
                  <Download className="w-4 h-4" />
                  <span>PDF 파일 다운로드</span>
                </button>

                <button
                  type="button"
                  onClick={() => printContractDocument(document.getElementById(`contract-doc-${contract.ContractID}`), contract.Title)}
                  className="flex items-center gap-2 px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl shadow-md transition-all cursor-pointer"
                  title="A4 다중 페이지 인쇄 출력 / PDF 저장"
                >
                  <Printer className="w-4 h-4" />
                  <span>PDF / 인쇄 출력</span>
                </button>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Signature Pad Modal */}
      <SignaturePadModal
        isOpen={isSignaturePadOpen}
        onClose={() => setIsSignaturePadOpen(false)}
        onConfirm={handleSignatureConfirmed}
        initialName={contract.UserName || ''}
        title={`'${contract.Title}' 전자계약 서명 날인`}
      />
    </div>
  );
};

export default PublicContractSignView;
